import { Button, getDefaultZIndex, Grid, Group, LoadingOverlay, Modal, Stack, Text } from '@mantine/core';

import { DeclarationHighValueGroupType } from '@e-origin/shared';

import { Select } from '@components';
import { useDeclarant } from '@hooks/declarant';
import { isHighValueDeclaration } from '@utils';
import { FieldRow, PolicyField, useFinalizeFieldRules } from '../policy';
import { useDeclarationProfileChange } from './useDeclarationProfileChange';

interface FinalizeProcedureProps {
  disabled: boolean;
  onSave: (newGroup: string) => Promise<void>;
}

const GROUP_FIELD = 'generalInfo.group';
const PROCEDURE_TYPE = 'goodShipmentGlobal.customsProcedureType';
const NATIONAL_PROCEDURE = 'goodShipmentGlobal.customsNationalProcedure';
const NATIONAL_PROCEDURE_CODE = 'goodShipmentGlobal.customsNationalProcedureCode';

export const FinalizeProcedure: React.FC<FinalizeProcedureProps> = ({ disabled, onSave }) => {
  const { form } = useFinalizeFieldRules();
  const { data: declarant, isLoading } = useDeclarant();

  const { formGroup, allowedProcedures, pendingGroup, setPendingGroup, requestChange, confirmChange } = useDeclarationProfileChange({ onSave });

  const headerOff = !form.values.goodShipmentGlobal.procedureHeader;

  const { rule, fieldProps } = useFinalizeFieldRules({ disabledWhen: headerOff });

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <Modal
        opened={!!pendingGroup}
        onClose={() => setPendingGroup(null)}
        title="Confirm declaration profile change"
        zIndex={getDefaultZIndex('modal') + 2}
        centered
      >
        <Stack>
          <Text size="sm">You have chosen the declaration profile {pendingGroup}. Any changes you made will be saved. Please confirm.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingGroup(null)}>
              Cancel
            </Button>
            <Button onClick={confirmChange}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      <FieldRow mb="sm">
        {rule(GROUP_FIELD).present && (
          <Grid.Col span={4}>
            <Select
              {...form.getInputProps(GROUP_FIELD)}
              label={rule(GROUP_FIELD).label}
              placeholder="Select a declaration profile"
              data={Object.values(DeclarationHighValueGroupType).map((value) => ({ value, label: value }))}
              disabled={disabled || headerOff}
              searchable
              data-pw={rule(GROUP_FIELD).dataPw}
              onChange={requestChange}
            />
          </Grid.Col>
        )}
        <PolicyField
          {...fieldProps('customsProcedureCurrent')}
          span={4}
          eucdmPath={'goodShipmentGlobal.customsProcedure.current'}
          includeList={allowedProcedures.length > 0 ? allowedProcedures : undefined}
        />
        <PolicyField {...fieldProps('customsProcedurePrevious')} span={4} eucdmPath={'goodShipmentGlobal.customsProcedure.previous'} />
      </FieldRow>

      <FieldRow mb="sm">
        <PolicyField {...fieldProps('goodShipmentGlobal.preference')} span={4} />
        <PolicyField {...fieldProps('declarationGoods.additionalProcedure')} span={4} eucdmPath={'goodsShipmentItems.additionalProcedure'} />
      </FieldRow>

      {/* The low-value gate reads the LIVE form group, so it is not derivable from the children and
          stays outside. FieldRow still owns the presence half. */}
      {!isHighValueDeclaration(formGroup) && (
        <FieldRow mb="sm">
          <PolicyField {...fieldProps(PROCEDURE_TYPE)} span={4} eucdmPath={'goodShipmentGlobal.customsProcedure.type'} />
          <PolicyField {...fieldProps(NATIONAL_PROCEDURE)} span={4} eucdm={false} />
          <PolicyField {...fieldProps(NATIONAL_PROCEDURE_CODE)} span={4} eucdm={false} />
        </FieldRow>
      )}
    </>
  );
};
